import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AuthUser, storeAuthSession } from '../utils/auth';

type AuthResponse = {
  accessToken: string;
  refreshToken: string;
  refreshTokenVersion?: number;
  user: AuthUser;
};

type ReauthDialogProps = {
  open: boolean;
  user: AuthUser | null;
  message?: string;
  onAuthenticated: () => void;
  onCancel?: () => void;
};

async function postAuth<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'Request failed');
  return data as T;
}

function normalizePhone(value: unknown): string {
  return extractDigits(value).slice(0, 10);
}

function extractDigits(value: unknown): string {
  return String(value || '').replace(/\D/g, '');
}

function phoneFromUser(user: AuthUser | null): string {
  return normalizePhone(
    user?.nexusUser?.mobilePhone
    || user?.nexusCaregiver?.mobilePhone
    || (/^\d{10}$/.test(String(user?.username || '')) ? user?.username : '')
  );
}

export default function ReauthDialog({ open, user, message, onAuthenticated, onCancel }: ReauthDialogProps) {
  const initialPhone = useMemo(() => phoneFromUser(user), [user]);
  const [phone, setPhone] = useState(initialPhone);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhone(initialPhone);
    setPassword('');
    setCode('');
    setMode('password');
    setCodeSent(false);
    setError(null);
  }, [initialPhone, open]);

  const completeAuth = (result: AuthResponse) => {
    if (user && result.user.id !== user.id) {
      setError('Sign in with the same account to continue this nursing assessment.');
      return;
    }
    storeAuthSession(result.accessToken, result.refreshToken, result.user, {
      preserveNursingSelection: true,
      refreshTokenVersion: result.refreshTokenVersion,
    });
    onAuthenticated();
  };

  const loginWithPassword = async () => {
    const normalizedPhone = normalizePhone(phone);
    if (normalizedPhone.length !== 10 || !password) {
      setError('Phone and password are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      completeAuth(await postAuth<AuthResponse>('/api/auth/password-login', {
        phone: normalizedPhone,
        password,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password login failed');
    } finally {
      setLoading(false);
    }
  };

  const sendCode = async () => {
    const normalizedPhone = normalizePhone(phone);
    if (normalizedPhone.length !== 10) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await postAuth('/api/auth/send-code', { phone: normalizedPhone });
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    const normalizedPhone = normalizePhone(phone);
    const normalizedCode = extractDigits(code).slice(0, 6);
    if (normalizedPhone.length !== 10 || normalizedCode.length !== 6) {
      setError('Phone and 6-digit code are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      completeAuth(await postAuth<AuthResponse>('/api/auth/verify-code', {
        phone: normalizedPhone,
        code: normalizedCode,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const submit = () => {
    if (mode === 'password') void loginWithPassword();
    else if (codeSent) void verifyCode();
    else void sendCode();
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>Sign in to continue</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {message || 'Your session expired. Sign in again to continue without losing your work.'}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Phone"
            value={phone}
            onChange={(event) => setPhone(normalizePhone(event.target.value))}
            fullWidth
            inputMode="tel"
            autoComplete="tel"
            disabled={loading}
          />
          {mode === 'password' ? (
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
              autoComplete="current-password"
              disabled={loading}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void loginWithPassword();
                }
              }}
            />
          ) : (
            <TextField
              label="Verification Code"
              value={code}
              onChange={(event) => setCode(extractDigits(event.target.value).slice(0, 6))}
              fullWidth
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={loading || !codeSent}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && codeSent) {
                  event.preventDefault();
                  void verifyCode();
                }
              }}
            />
          )}
          <Box>
            <Link
              component="button"
              type="button"
              onClick={() => {
                setMode((current) => (current === 'password' ? 'code' : 'password'));
                setError(null);
              }}
              sx={{ fontSize: '0.875rem' }}
            >
              {mode === 'password' ? 'Use a verification code instead' : 'Use password instead'}
            </Link>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onCancel && <Button onClick={onCancel} disabled={loading}>Cancel</Button>}
        <Button variant="contained" onClick={submit} disabled={loading}>
          {loading ? <CircularProgress color="inherit" size={20} /> : mode === 'code' && !codeSent ? 'Send Code' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
